"use client";

import React from "react";
import Link from "next/link";
import Image from "next/image";
import { useParams } from "next/navigation";
import appendImageUrlFromAPI from "@/utils/appendImageUrlFromAPI";
import slugify from "@/utils/slugify";
import { getProductHoverColor } from "@/utils/getProductColor";
import { useProduct } from "@/context/ProductContext";
import { useProductTranslation } from "@/hooks/useProductTranslation";
import styles from "./productCard.module.css";

interface ProductCardProps {
  product?: any;
  isLoading?: boolean;
  isMini?: boolean;
  hoverColor?: string;
  colorConfig?: any;
  locale?: string;
}

export default function ProductCard({
  product,
  isLoading,
  isMini,
  hoverColor,
  colorConfig,
  locale,
}: ProductCardProps) {
  const { productConfig: ctxConfig } = useProduct();
  const params = useParams();
  const currentLocale = locale || (params?.locale as string) || "vi";

  const rawTitle = product?.ten_vt || "";
  const rawPackaging = product?.ten_dvt || product?.don_vi_tinh || "";

  const translated = useProductTranslation(
    {
      title: rawTitle,
      packaging: rawPackaging,
    },
    currentLocale
  );

  if (isLoading || !product) {
    return (
      <div className={`${styles.card} ${isMini ? styles.mini : ""}`}>
        <div className={`${styles.imageWrap} ${styles.skeleton}`} />
        <div className={styles.info}>
          <div className={`${styles.skeletonText} ${styles.skeletonTitle}`} />
          <div className={`${styles.skeletonText} ${styles.skeletonSubtitle}`} />
        </div>
      </div>
    );
  }

  // Luôn dùng tên tiếng Việt gốc cho slug để khớp chính xác URL động
  const productUrl = `/${currentLocale}/san-pham/${slugify(product?.ten_vt)}-${product?.ma_vt}`;
  const imgSrc = appendImageUrlFromAPI({
    src: product?.picture,
    size: "m",
  });

  const effectiveConfig = colorConfig || ctxConfig;
  const hoverBg = hoverColor || getProductHoverColor(product, effectiveConfig);

  const displayTitle = translated.title || product?.ten_vt;

  // Pill tags at top-left (e.g., "120g", "200g", "Thùng", "Gói")
  const tags: string[] = [];
  if (translated.packaging || product?.ten_dvt) {
    tags.push(translated.packaging || product.ten_dvt);
  } else if (product?.don_vi_tinh) {
    tags.push(product.don_vi_tinh);
  } else if (product?.weight) {
    tags.push(product.weight);
  }

  return (
    <Link
      href={productUrl}
      className={`${styles.card} ${isMini ? styles.mini : ""}`}
      style={{ "--hover-color": hoverBg } as React.CSSProperties}
    >
      {/* Top-left pill tags */}
      {tags.length > 0 && !isMini && (
        <div className={styles.tagsRow}>
          {tags.map((tag, idx) => (
            <span key={idx} className={styles.tagPill}>
              {tag}
            </span>
          ))}
        </div>
      )}

      {/* Product Image */}
      <div className={styles.imageWrap}>
        {imgSrc ? (
          <Image
            src={imgSrc}
            alt={displayTitle || "Product"}
            fill
            sizes="(max-width: 480px) 50vw, (max-width: 768px) 33vw, 25vw"
            className={styles.image}
          />
        ) : (
          <div className={styles.placeholder}>
            <svg
              width="48"
              height="48"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#ccc"
              strokeWidth="1"
            >
              <rect x="3" y="3" width="18" height="18" rx="2" ry="2" />
              <circle cx="8.5" cy="8.5" r="1.5" />
              <polyline points="21 15 16 10 5 21" />
            </svg>
          </div>
        )}
      </div>

      {/* Info Section */}
      <div className={styles.info}>
        <h3 className={styles.name}>{displayTitle}</h3>
        <span className={styles.detailText}>
          {currentLocale === "en" ? "View details" : "Xem chi tiết"}
        </span>
      </div>
    </Link>
  );
}
